import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react"
import { ArrowUp, Square, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import { Textarea } from "../Textarea/Textarea"

export type ChatInputPrimaryAction = "send" | "stop" | "cancel"

/** ~22ms/char: a 42-character line lands in ~924ms. Fast enough to read as
 *  someone typing, short enough that nobody waits for it. */
const TYPE_STEP_MS = 22

export interface ChatInputHandle {
  focus: () => void
  /**
   * Type `text` into an EMPTY composer, character by character, as if someone
   * were sitting there writing it. An attention cue, not a paste.
   *
   * Refused when the user has a draft — a suggestion never overwrites their
   * words. Touching the textarea mid-flight ends it two different ways on
   * purpose: a POINTER DOWN lands the rest of the line at once (a click means
   * "let me at this", and half a sentence is worse than none), while an actual
   * KEYSTROKE stops it dead and keeps exactly what they typed.
   * `prefers-reduced-motion` sets the whole line instantly.
   */
  typeIfEmpty: (text: string) => void
}

export interface ChatInputProps {
  id?: string
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  onSubmit?: (value: string) => void
  onStop?: () => void
  onCancel?: () => void
  primaryAction?: ChatInputPrimaryAction
  placeholder?: string
  disabled?: boolean
  isMobileLayout?: boolean
  bannerSlot?: ReactNode
  editingSlot?: ReactNode
  /** Full-width row between the textarea and the chrome row (the chart-context
   * tick box). Its own row so the typing area is never squeezed. */
  contextRowSlot?: ReactNode
  /** Chrome-row controls that sit left of the send button (e.g. the effort selector). */
  leadingChromeSlot?: ReactNode
  /** Far-left chrome-row control (the Auto-verify switch). An empty spacer when absent. */
  chromeStartSlot?: ReactNode
  overlaySlot?: ReactNode
  trailingChromeSlot?: ReactNode
  className?: string
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      id,
      value: controlled,
      defaultValue = "",
      onChange,
      onSubmit,
      onStop,
      onCancel,
      primaryAction = "send",
      placeholder = "Ask anything…",
      disabled = false,
      isMobileLayout = false,
      bannerSlot,
      editingSlot,
      contextRowSlot,
      leadingChromeSlot,
      chromeStartSlot,
      overlaySlot,
      trailingChromeSlot,
      className,
    },
    ref,
  ) => {
    const [internal, setInternal] = useState(defaultValue)
    const value = controlled ?? internal
    const setValue = useCallback(
      (next: string) => {
        if (controlled === undefined) setInternal(next)
        onChange?.(next)
      },
      [controlled, onChange],
    )

    const textareaRef = useRef<HTMLTextAreaElement | null>(null)

    // The target text rides with the timer so a fast-forward knows what to land.
    const typingRef = useRef<{
      timer: ReturnType<typeof setInterval>
      text: string
    } | null>(null)

    const stopTyping = useCallback(() => {
      const run = typingRef.current
      if (!run) return
      clearInterval(run.timer)
      typingRef.current = null
    }, [])

    /** Land the rest of the line now, rather than leaving a fragment behind. */
    const finishTyping = useCallback(() => {
      const run = typingRef.current
      if (!run) return
      stopTyping()
      setValue(run.text)
    }, [stopTyping, setValue])

    // A composer that unmounts mid-flight (tab switch, sheet close) would leave
    // the interval running against a dead component.
    useEffect(() => stopTyping, [stopTyping])

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        // The user is writing: their keystrokes win, and nothing overwrites them.
        stopTyping()
        setValue(e.target.value)
      },
      [stopTyping, setValue],
    )

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      typeIfEmpty: (text: string) => {
        if (value.trim().length > 0 || !text) return
        stopTyping()
        // Checked here rather than in tokens.css: this is the value of a
        // controlled <textarea>, not a keyframe, so there is nothing for a
        // prefers-reduced-motion rule to suppress. The suggestion still lands —
        // just without the theatre.
        const reduced =
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
        if (reduced) {
          setValue(text)
          return
        }
        let i = 0
        const timer = setInterval(() => {
          i += 1
          setValue(text.slice(0, i))
          if (i >= text.length) stopTyping()
        }, TYPE_STEP_MS)
        typingRef.current = { timer, text }
      },
    }))

    useEffect(() => {
      if (!isMobileLayout) return
      const t = textareaRef.current
      if (!t) return
      t.style.height = "auto"
      t.style.height = Math.min(t.scrollHeight, 128) + "px"
    }, [value, isMobileLayout])

    const canSubmit =
      value.trim().length > 0 && !disabled && primaryAction === "send"

    const submit = useCallback(() => {
      const text = value.trim()
      if (!text || primaryAction !== "send") return
      // Load-bearing: a tick landing after the clear below would write the
      // suggestion back in, leaving a ghost draft behind the sent message.
      stopTyping()
      onSubmit?.(text)
      setValue("")
    }, [value, primaryAction, onSubmit, setValue, stopTyping])

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault()
          submit()
        } else if (e.key === "Escape") {
          if (primaryAction === "stop") {
            e.preventDefault()
            onStop?.()
          }
        }
      },
      [submit, primaryAction, onStop],
    )

    const actionButton = (() => {
      if (primaryAction === "cancel") {
        return (
          <Button
            type="button"
            size="icon-sm"
            variant="destructive"
            onClick={onCancel}
            aria-label="Cancel"
          >
            <X />
          </Button>
        )
      }
      if (primaryAction === "stop") {
        return (
          <Button
            type="button"
            size="icon-sm"
            variant="secondary"
            onClick={onStop}
            aria-label="Stop"
          >
            <Square className="fill-current" />
          </Button>
        )
      }
      return (
        <Button
          type="button"
          size="icon-sm"
          disabled={!canSubmit}
          onClick={submit}
          aria-label="Send message"
        >
          <ArrowUp />
        </Button>
      )
    })()

    if (overlaySlot) {
      return (
        <div id={id} className={cn("shrink-0 border-t border-border p-2 bg-background", className)}>
          {overlaySlot}
        </div>
      )
    }

    return (
      <div id={id} className={cn("shrink-0 border-t border-border p-2 bg-background", className)}>
        {bannerSlot}
        <div
          className={cn(
            "relative flex flex-col rounded-lg border border-border bg-card overflow-hidden",
            "transition-colors focus-within:border-ring/60",
          )}
        >
          {editingSlot}
          {isMobileLayout ? (
            <div className="flex items-end gap-1 pl-3 pr-2 pt-2 pb-1.5">
              <Textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onPointerDown={finishTyping}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder={placeholder}
                disabled={disabled}
                className={cn(
                  "flex-1 min-h-9 max-h-32 resize-none py-1.5 px-2 text-body leading-5",
                  "border-0 bg-transparent dark:bg-transparent shadow-none",
                  "focus-visible:ring-0 focus-visible:outline-none",
                  disabled && "opacity-60 cursor-not-allowed",
                )}
              />
              {actionButton}
            </div>
          ) : (
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={handleChange}
              onPointerDown={finishTyping}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                "min-h-20 max-h-32 resize-none pt-2.5 pb-2 px-3 text-body leading-5",
                "border-0 bg-transparent dark:bg-transparent shadow-none",
                "focus-visible:ring-0 focus-visible:outline-none",
                disabled && "opacity-60 cursor-not-allowed",
              )}
            />
          )}
          {contextRowSlot}
          <div className="flex items-center justify-between gap-2 shrink-0 pl-3 pr-2 py-1 border-t border-border/40 bg-muted/30">
            {chromeStartSlot ?? <div />}
            <div className="flex items-center gap-1 shrink-0">
              {leadingChromeSlot}
              {isMobileLayout ? trailingChromeSlot ?? null : actionButton}
            </div>
          </div>
        </div>
      </div>
    )
  },
)
ChatInput.displayName = "ChatInput"
